import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoPencil, IoChevronDown } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getUserProfile, updateUserProfile, getRealtorProfile, updateRealtorProfile } from '../../../services/userProfileService';
import { storage } from '../../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';

const GENDER_OPTIONS = ['선택 안 함', '남자', '여자'];
const ACCOUNT_TYPE_OPTIONS = ['개인', '사업자'];
const BANK_OPTIONS = [
  '국민은행', '신한은행', '우리은행', '하나은행', 'NH농협', 'IBK기업', '카카오뱅크', '토스뱅크',
  '케이뱅크', 'SC제일은행', '씨티은행', '대구은행', '부산은행', '광주은행', '전북은행', '경남은행',
  '새마을금고', '우체국', '기타',
];

const onlyDigits = (v) => (v || '').replace(/\D/g, '');

const formatPhone = (raw) => {
  const v = onlyDigits(raw).slice(0, 11);
  if (v.length < 4) return v;
  if (v.length < 7) return `${v.slice(0, 3)}-${v.slice(3)}`;
  if (v.length === 10) return `${v.slice(0, 3)}-${v.slice(3, 6)}-${v.slice(6)}`;
  if (v.length < 11) return `${v.slice(0, 3)}-${v.slice(3, 6)}-${v.slice(6)}`;
  return `${v.slice(0, 3)}-${v.slice(3, 7)}-${v.slice(7)}`;
};

const formatSsn = (raw) => {
  const v = onlyDigits(raw).slice(0, 13);
  if (v.length <= 6) return v;
  return `${v.slice(0, 6)}-${v.slice(6)}`;
};

const formatBizNo = (raw) => {
  const v = onlyDigits(raw).slice(0, 10);
  if (v.length <= 3) return v;
  if (v.length <= 5) return `${v.slice(0, 3)}-${v.slice(3)}`;
  return `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5)}`;
};

const ProfileEdit = () => {
  const navigate = useNavigate();
  const { userData, refreshSession } = useAuth();
  const uid = userData?.uid;

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoURL, setPhotoURL] = useState('');
  const fileInputRef = useRef(null);
  const bankBookRef = useRef(null);
  const bizLicenseRef = useRef(null);
  const [bankBookFile, setBankBookFile] = useState(null);
  const [bizLicenseFile, setBizLicenseFile] = useState(null);
  const [bankBookUrl, setBankBookUrl] = useState('');
  const [bizLicenseUrl, setBizLicenseUrl] = useState('');
  const [docUploading, setDocUploading] = useState(false);
  const [phoneModal, setPhoneModal] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [verifySent, setVerifySent] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    birthYear: '',
    gender: '선택 안 함',
    accountType: '개인',
    office: '',
    ssn: '',
    bank: '',
    accountNumber: '',
    accountHolder: '',
    phoneVerifiedAt: '',
  });

  const userName = form.name || userData?.name || '사용자';
  const initial = userName.slice(0, 2);
  const businessName = form.office || userData?.businessName || '';

  // Firestore에서 로드
  useEffect(() => {
    if (!uid) return;
    const load = async () => {
      const [user, realtor] = await Promise.all([
        getUserProfile(uid),
        getRealtorProfile(uid),
      ]);
      setEmail(user?.email || '');
      setPhotoURL(user?.photoURL || '');
      setForm((prev) => ({
        ...prev,
        name: user?.name || '',
        phone: user?.phone || '',
        birthYear: user?.birthYear || '',
        gender: user?.gender || '선택 안 함',
        phoneVerifiedAt: user?.phoneVerifiedAt || '',
        office: realtor?.businessName || '',
        accountType: realtor?.accountType || '개인',
        ssn: realtor?.ssn || '',
        bank: realtor?.bankName || '',
        accountNumber: realtor?.accountNumber || '',
        accountHolder: realtor?.accountHolder || '',
      }));
      setBankBookUrl(realtor?.bankBookUrl || '');
      setBizLicenseUrl(realtor?.businessLicenseUrl || '');
    };
    load().catch(() => {});
  }, [uid]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uid) return;
    setUploading(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.15,
        maxWidthOrHeight: 400,
        useWebWorker: true,
        initialQuality: 0.6,
      });
      const storageRef = ref(storage, `avatars/${uid}_${Date.now()}`);
      await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(storageRef);
      await updateUserProfile(uid, { photoURL: url });
      setPhotoURL(url);
      await refreshSession();
    } catch (err) {
      alert('사진 업로드 실패: ' + (err.message || ''));
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleDocUpload = async (file, path, setter) => {
    if (!file || !uid) return '';
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const storageRef = ref(storage, `realtors/${uid}/${path}.${ext}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    setter(url);
    return url;
  };

  const handleSave = async () => {
    if (!uid) return;
    setSaving(true);
    try {
      let bankBookNewUrl = bankBookUrl;
      let bizLicenseNewUrl = bizLicenseUrl;
      if (form.accountType === '사업자') {
        setDocUploading(true);
        if (bankBookFile) bankBookNewUrl = await handleDocUpload(bankBookFile, 'bank_book', setBankBookUrl);
        if (bizLicenseFile) bizLicenseNewUrl = await handleDocUpload(bizLicenseFile, 'business_license', setBizLicenseUrl);
        setDocUploading(false);
      }
      await updateUserProfile(uid, {
        name: form.name,
        phone: form.phone,
        birthYear: form.birthYear,
        gender: form.gender,
      });
      await updateRealtorProfile(uid, {
        businessName: form.office,
        accountType: form.accountType,
        ssn: form.ssn,
        bankName: form.bank,
        accountNumber: form.accountNumber,
        accountHolder: form.accountHolder,
        bankBookUrl: bankBookNewUrl,
        businessLicenseUrl: bizLicenseNewUrl,
      });
      await refreshSession();
      alert('저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + (err.message || ''));
    } finally {
      setSaving(false);
      setDocUploading(false);
    }
  };

  const handleSendVerify = () => {
    if (onlyDigits(newPhone).length < 10) { alert('휴대폰 번호를 확인해주세요.'); return; }
    // TODO: SMS 실연동
    setVerifySent(true);
    alert('인증번호가 발송되었습니다. (데모: 아무 번호나 입력)');
  };

  const handleConfirmVerify = async () => {
    if (verifyCode.length < 4) { alert('인증번호를 입력해주세요.'); return; }
    const today = new Date().toISOString().slice(0, 10);
    try {
      await updateUserProfile(uid, { phone: newPhone, phoneVerifiedAt: today });
      setForm((prev) => ({ ...prev, phone: newPhone, phoneVerifiedAt: today }));
      setPhoneModal(false);
      setVerifySent(false);
      setVerifyCode('');
      setNewPhone('');
      await refreshSession();
      alert('휴대폰 번호가 변경되었습니다.');
    } catch (err) {
      alert('변경 실패: ' + (err.message || ''));
    }
  };

  return (
    <Container>
      {/* 프로필 카드 */}
      <ProfileCard>
        <AvatarWrap onClick={() => !uploading && fileInputRef.current?.click()} style={{ cursor: 'pointer' }}>
          {photoURL ? (
            <AvatarImg src={photoURL} alt="프로필" />
          ) : (
            <Avatar>{initial}</Avatar>
          )}
          {uploading ? (
            <SpinnerOverlay><SpinnerDot /></SpinnerOverlay>
          ) : (
            <EditBadge><IoPencil size={12} color="#fff" /></EditBadge>
          )}
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
          />
        </AvatarWrap>
        <ProfileInfo>
          <UserName>{userName}</UserName>
          <BusinessNameText>{businessName}</BusinessNameText>
        </ProfileInfo>
      </ProfileCard>

      {/* 아이디 */}
      <FieldGroup>
        <FieldLabel>아이디</FieldLabel>
        <Input value={email} readOnly style={{ color: '#9CA3AF' }} />
        <HintText>가입 시 등록된 아이디이며 변경할 수 없습니다.</HintText>
      </FieldGroup>

      {/* 담당자 이름 */}
      <FieldGroup>
        <FieldLabel>담당자 이름</FieldLabel>
        <Input
          placeholder="한글, 영문, 숫자 1~10자"
          value={form.name}
          onChange={handleChange('name')}
        />
      </FieldGroup>

      {/* 휴대전화 */}
      <FieldGroup>
        <FieldLabel>휴대전화</FieldLabel>
        <SubLabel>휴대폰 본인확인 {form.phoneVerifiedAt ? `(${form.phoneVerifiedAt} 인증)` : '(미인증)'}</SubLabel>
        <PhoneRow>
          <Input
            value={formatPhone(form.phone)}
            onChange={(e) => setForm((prev) => ({ ...prev, phone: onlyDigits(e.target.value) }))}
            inputMode="numeric"
            style={{ flex: 1 }}
          />
          <PhoneBtn onClick={() => setPhoneModal(true)}>휴대폰 변경</PhoneBtn>
        </PhoneRow>
      </FieldGroup>

      {/* 출생년도 / 성별 */}
      <FieldGroup>
        <FieldLabel>출생년도 / 성별</FieldLabel>
        <HintText>본인인증 연동 시 자동 반영되며, 미연동 시 직접 입력할 수 있습니다.</HintText>
        <SegmentRow>
          {GENDER_OPTIONS.map((opt) => (
            <SegmentBtn
              key={opt}
              $active={form.gender === opt}
              onClick={() => setForm((prev) => ({ ...prev, gender: opt }))}
            >
              {opt}
            </SegmentBtn>
          ))}
        </SegmentRow>
        <Input
          placeholder="출생년도 (예: 1990)"
          value={form.birthYear}
          onChange={(e) => setForm((prev) => ({ ...prev, birthYear: onlyDigits(e.target.value).slice(0, 4) }))}
          inputMode="numeric"
        />
      </FieldGroup>

      {/* 부동산 중개 사무소 */}
      <FieldGroup>
        <FieldLabel>부동산 중개 사무소</FieldLabel>
        <Input
          placeholder="부동산 상호"
          value={form.office}
          onChange={handleChange('office')}
        />
      </FieldGroup>

      {/* 리워드 지급 계좌 확인 */}
      <FieldGroup>
        <FieldLabel>리워드 지급 계좌 확인</FieldLabel>
        <SegmentRow>
          {ACCOUNT_TYPE_OPTIONS.map((opt) => (
            <SegmentBtn
              key={opt}
              $active={form.accountType === opt}
              onClick={() => setForm((prev) => ({ ...prev, accountType: opt }))}
            >
              {opt}
            </SegmentBtn>
          ))}
        </SegmentRow>
        <HintText>본인인증 연동 시 자동 반영되며, 미연동 시 직접 입력할 수 있습니다.</HintText>
        <HintText>
          개인은 원천세(3.3%) 공제, 사업자 회원은 부가세(10%) 포함가 기준 세금계산서 발행 정산 지급합니다.
        </HintText>
      </FieldGroup>

      {/* 주민등록번호 / 사업자등록번호 */}
      <FieldGroup>
        <FieldLabel>
          {form.accountType === '사업자' ? '사업자등록번호' : '주민등록번호 13자리'}
        </FieldLabel>
        <Input
          placeholder={form.accountType === '사업자' ? '예시) 123-45-67890' : '예시) 900102-1234567'}
          value={form.accountType === '사업자' ? formatBizNo(form.ssn) : formatSsn(form.ssn)}
          onChange={(e) => setForm((prev) => ({ ...prev, ssn: onlyDigits(e.target.value) }))}
          inputMode="numeric"
        />
      </FieldGroup>

      {/* 사업자 서류 업로드 */}
      {form.accountType === '사업자' && (
        <>
          <FieldGroup>
            <FieldLabel>통장사본</FieldLabel>
            <input type="file" accept="image/*,application/pdf" ref={bankBookRef} hidden
              onChange={(e) => setBankBookFile(e.target.files[0])} />
            <UploadBox type="button" onClick={() => bankBookRef.current?.click()}>
              <UploadText>{bankBookFile ? bankBookFile.name : (bankBookUrl ? '등록됨 · 다시 선택' : '+ 통장사본 파일 추가')}</UploadText>
            </UploadBox>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>사업자등록증</FieldLabel>
            {bizLicenseUrl ? (
              <LicenseViewLink href={bizLicenseUrl} target="_blank" rel="noopener noreferrer">
                사업자등록증 보기
              </LicenseViewLink>
            ) : (
              <UploadBox as="div">
                <UploadText>등록된 파일 없음</UploadText>
              </UploadBox>
            )}
            <HintText>파일 변경은 본사에 문의하세요.</HintText>
          </FieldGroup>
        </>
      )}

      {/* 은행 선택 */}
      <FieldGroup>
        <FieldLabel>은행 선택</FieldLabel>
        <SelectWrap>
          <Select value={form.bank} onChange={handleChange('bank')}>
            <option value="">은행 선택</option>
            {BANK_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
          </Select>
          <SelectIcon><IoChevronDown size={18} color="#9CA3AF" /></SelectIcon>
        </SelectWrap>
      </FieldGroup>

      {/* 계좌 번호 */}
      <FieldGroup>
        <FieldLabel>계좌 번호</FieldLabel>
        <Input
          placeholder="계좌 번호"
          value={form.accountNumber}
          onChange={(e) => setForm((prev) => ({ ...prev, accountNumber: onlyDigits(e.target.value) }))}
          inputMode="numeric"
        />
      </FieldGroup>

      {/* 계좌주명 */}
      <FieldGroup>
        <FieldLabel>계좌주명</FieldLabel>
        <Input
          placeholder="예금주명"
          value={form.accountHolder}
          onChange={handleChange('accountHolder')}
        />
      </FieldGroup>

      {/* 버튼 */}
      <BtnGroup>
        <SaveBtn onClick={handleSave} disabled={saving || docUploading}>{saving || docUploading ? '저장 중...' : '저장'}</SaveBtn>
        <PasswordBtn onClick={() => navigate('/change-password')}>비밀번호 변경</PasswordBtn>
      </BtnGroup>

      {phoneModal && (
        <ModalBackdrop onClick={() => setPhoneModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>휴대폰 변경</ModalTitle>
            <FieldLabel>새 휴대폰 번호</FieldLabel>
            <PhoneRow>
              <Input
                placeholder="010-0000-0000"
                value={formatPhone(newPhone)}
                onChange={(e) => setNewPhone(onlyDigits(e.target.value))}
                inputMode="numeric"
                style={{ flex: 1 }}
              />
              <PhoneBtn onClick={handleSendVerify}>{verifySent ? '재발송' : '인증번호'}</PhoneBtn>
            </PhoneRow>
            {verifySent && (
              <>
                <FieldLabel style={{ marginTop: 12 }}>인증번호</FieldLabel>
                <Input
                  placeholder="인증번호 6자리"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(onlyDigits(e.target.value).slice(0, 6))}
                  inputMode="numeric"
                />
              </>
            )}
            <BtnGroup>
              <SaveBtn onClick={handleConfirmVerify} disabled={!verifySent}>인증 완료</SaveBtn>
              <PasswordBtn onClick={() => { setPhoneModal(false); setVerifySent(false); setVerifyCode(''); setNewPhone(''); }}>취소</PasswordBtn>
            </BtnGroup>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Container>
  );
};

export default ProfileEdit;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ProfileCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.realtor.headerBg};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const AvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.realtor.avatarBg};
  color: ${({ theme }) => theme.realtor.avatarText};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const AvatarImg = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  object-fit: cover;
`;

const HiddenInput = styled.input`
  display: none;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: ${({ theme }) => theme.radius.full};
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
`;

const SpinnerDot = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const EditBadge = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const BusinessNameText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 2px;
`;

const FieldGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SubLabel = styled.p`
  font-size: 13px;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  color: #9CA3AF;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const HintText = styled.p`
  font-size: 13px;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  color: #9CA3AF;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  box-sizing: border-box;

  &::placeholder { color: #9CA3AF; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const PhoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const PhoneBtn = styled.button`
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SegmentRow = styled.div`
  display: flex;
  background: #F3F4F6;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SegmentBtn = styled.button`
  flex: 1;
  padding: 8px 0;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.semibold : theme.fontWeight.regular};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  cursor: pointer;
`;

const SelectWrap = styled.div`
  position: relative;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  box-sizing: border-box;
  appearance: none;
  background: #fff;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const SelectIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const BtnGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
`;

const PasswordBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
`;

const UploadBox = styled.button`
  width: 100%;
  padding: 14px 16px;
  border: 1px dashed #D1D5DB;
  border-radius: 10px;
  background: #F9FAFB;
  color: #6B7280;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
`;

const UploadText = styled.span`
  font-size: 14px;
  color: #6B7280;
`;

const LicenseViewLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #D1D5DB;
  border-radius: 10px;
  background: #fff;
  color: #2563EB;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  &:hover { background: #F3F4F6; text-decoration: underline; }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  width: 100%;
  max-width: 400px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 16px;
`;
